import React, { useContext, useEffect, useState } from 'react';
import { Label, Card, DataTable } from '@cargomatic/ui';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import CustomerAPIUtils, { getCustomerV2, sendShipperRateCardEmail, } from 'utils/CustomersAPIUtils';
import LoadingIndicator from 'components/v2/LoadingIndicator';
import { showToast } from 'stores/ToastStore';
import { LocaleContext } from 'providers/locale-settings-context';
import { FSC_TYPE } from 'constants/fsc/fsc';
import RatesSettingsLayout from '../modules/user/rates/RatesSettingsLayout';
import SendRateCardEmailModal from 'components/modules/user/rates/SendRateCardEmailModal';
import useModal from 'hooks/useModal';
import { getRateCardEmailRecipient } from 'helpers/shipments';
import LoadingCircle from 'components/ui/LoadingCircle';
import { unescapeAmpersand } from 'helpers/helper_functions';
import SessionStore from 'stores/SessionStore';
import { fetchDocuments, getPdfUrl, handleDownloadRateCard, } from 'utils/documentUtils';
import { useLaneRates } from 'hooks/useLaneRate';
import { getColumnsForRateCardPage } from 'components/modules/pricing/rate-conditions/lane-rates/columns';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
const pagination = { pageIndex: 0, pageSize: 1000 };
const sorting = [];
const RateCard = () => {
    const { t } = useTranslation();
    const { id: accountId } = useParams();
    const { formatDate } = useContext(LocaleContext);
    const [account, setAccount] = useState({});
    const [loading, setLoading] = useState(false);
    const [rateCardGenerationInProgress, setRateCardGenerationInProgress] = useState(false);
    const [recentRateCard, setRecentRateCard] = useState({});
    const [translateOptions, setTranslateOptions] = useState({});
    const { visible: rateCardEmailVisible, show: showRateCardEmailModal, close: closeRateCardEmailModal, } = useModal();
    const filters = [
        { id: 'status', value: ['confirmed'] },
        { id: 'customerId', value: [accountId] },
    ];
    const { data, isLoading } = useLaneRates({
        sorting,
        pagination,
        filters,
    });
    const [rowSelection, setRowSelection] = useState({});
    const columns = getColumnsForRateCardPage();
    const table = useReactTable({
        columns: columns,
        data: (data === null || data === void 0 ? void 0 : data.laneRates) || [],
        enableRowSelection: true,
        getRowId: (row) => row === null || row === void 0 ? void 0 : row.laneRateId,
        state: {
            rowSelection,
        },
        getCoreRowModel: getCoreRowModel(),
    });
    useEffect(() => {
        fetchAccount();
        generateLocalizationSlugs();
    }, []);
    useEffect(() => {
        var _a;
        if (((_a = data === null || data === void 0 ? void 0 : data.laneRates) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            const initialSelection = {};
            data.laneRates.forEach((laneRate) => {
                initialSelection[laneRate === null || laneRate === void 0 ? void 0 : laneRate.laneRateId] = true;
            });
            setRowSelection(initialSelection);
        }
    }, [data]);
    async function fetchAccount() {
        try {
            setLoading(true);
            const account = await getCustomerV2(accountId);
            const documentType = (account === null || account === void 0 ? void 0 : account._type) === 'Shipper'
                ? 'customer_rate_card'
                : 'carrier_rate_card';
            const rateCardData = await fetchDocuments(accountId, documentType);
            setRecentRateCard(rateCardData);
            setAccount(account);
        }
        catch (e) {
            showToast('', t('FAILED_FETCHING_SHIPPER_DETAILS'), 'error');
        }
        finally {
            setLoading(false);
        }
    }
    const generateLocalizationSlugs = () => {
        const currency = t('CURRENCY_SIGN');
        const initFscTypes = {
            VIA_INTEGRATION: t('FS_SERVICE'),
            FLAT_DOLLAR: t('FLAT'),
            FLAT_PERCENT: t('PERCENTAGE_LH'),
            VARIABLE_DOLLAR_PER_MILE: t('VARIABLE_AMOUNT_PER_MILE'),
        };
        const fscTypes = Object.keys(FSC_TYPE).reduce((acc, slug) => {
            if (!initFscTypes[slug]) {
                const translation = t(slug);
                if (slug === translation) {
                    acc[slug] = FSC_TYPE[slug];
                }
                else {
                    acc[slug] = translation;
                }
            }
            return acc;
        }, initFscTypes);
        setTranslateOptions({
            currency,
            fscTypes,
        });
    };
    const generateRateCard = async () => {
        var _a, _b, _c;
        try {
            setRateCardGenerationInProgress(true);
            const createdDate = formatDate(Date.now());
            const shipperName = unescapeAmpersand(account === null || account === void 0 ? void 0 : account.account_name);
            const billToName = unescapeAmpersand(((_a = account === null || account === void 0 ? void 0 : account.billing_address) === null || _a === void 0 ? void 0 : _a.name) || shipperName);
            const laneRates = ((data === null || data === void 0 ? void 0 : data.laneRates) || []).map(({ laneRateId }) => laneRateId);
            // Common fields
            const commonPayload = {
                customerId: account === null || account === void 0 ? void 0 : account._id,
                createdDate,
                billToName,
                localizationSlugs: translateOptions,
                languageCodeId: LOCALE_CODE,
            };
            // Shipper-specific payload
            const shipperPayload = Object.assign(Object.assign({}, commonPayload), { shipperName: unescapeAmpersand(account === null || account === void 0 ? void 0 : account.account_name), billToName: unescapeAmpersand(((_b = account === null || account === void 0 ? void 0 : account.billing_address) === null || _b === void 0 ? void 0 : _b.name) || (account === null || account === void 0 ? void 0 : account.account_name)), salesRep: account === null || account === void 0 ? void 0 : account.sales_rep, laneRates });
            // Carrier-specific payload
            const carrierPayload = Object.assign(Object.assign({}, commonPayload), { carrierName: unescapeAmpersand(account === null || account === void 0 ? void 0 : account.name), billToName: unescapeAmpersand(((_c = account === null || account === void 0 ? void 0 : account.billing_address) === null || _c === void 0 ? void 0 : _c.name) || (account === null || account === void 0 ? void 0 : account.name)) });
            const userType = account === null || account === void 0 ? void 0 : account._type;
            const payload = userType === 'Shipper' ? shipperPayload : carrierPayload;
            const result = await CustomerAPIUtils.generateRateCard(payload);
            const { documentKey, createdAt: generatedOn } = result;
            const pdfUrl = await getPdfUrl(documentKey);
            setRecentRateCard({ documentKey, generatedOn, pdfUrl });
        }
        catch (e) {
            showToast('', t('GENERATE_AGAIN_RATE_CARD'), 'error');
        }
        finally {
            setRateCardGenerationInProgress(false);
        }
    };
    const sendRateCardEmail = (recipients) => {
        sendShipperRateCardEmail(accountId, recipients, recentRateCard);
    };
    const getRateCardEmailRecipientList = (account) => {
        var _a;
        const loggedInUserEmail = ((_a = SessionStore.getUser()) === null || _a === void 0 ? void 0 : _a.email) || '';
        const recipients = getRateCardEmailRecipient(account);
        return [recipients, loggedInUserEmail].filter(Boolean).join(', ').trim();
    };
    const renderMenu = () => (React.createElement("div", { className: "align-self-end" },
        React.createElement(Dropdown, { useIcon: true },
            React.createElement(Dropdown.Button, { onClick: generateRateCard, disabled: rateCardGenerationInProgress }, (recentRateCard === null || recentRateCard === void 0 ? void 0 : recentRateCard.pdfUrl) ? t('REGENERATE') : t('GENERATE')),
            (recentRateCard === null || recentRateCard === void 0 ? void 0 : recentRateCard.pdfUrl) && (React.createElement(Dropdown.Button, { onClick: showRateCardEmailModal, disabled: rateCardGenerationInProgress }, t('SEND_EMAIL'))))));
    const renderDownloadLink = () => {
        if (rateCardGenerationInProgress) {
            return React.createElement(LoadingCircle, { text: t('RATE_CARD_GENERATION_IN_PROGRESS') });
        }
        if (recentRateCard === null || recentRateCard === void 0 ? void 0 : recentRateCard.pdfUrl) {
            return (React.createElement("a", { onClick: () => handleDownloadRateCard(recentRateCard === null || recentRateCard === void 0 ? void 0 : recentRateCard.pdfUrl) }, t('DOWNLOAD_RATE_CARD')));
        }
        return React.createElement("p", null, t('RATE_CARD_NOT_AVAILABLE'));
    };
    const renderRecentRateCard = () => {
        return (React.createElement(React.Fragment, null,
            renderDownloadLink(),
            !rateCardGenerationInProgress && (React.createElement(DescriptionListItem, { label: t('GENERATED_ON') },
                React.createElement(Label, null, formatDate(recentRateCard === null || recentRateCard === void 0 ? void 0 : recentRateCard.generatedOn) || '-'))),
            renderMenu()));
    };
    const renderLaneRatesTable = () => {
        return React.createElement(DataTable, { table: table, isLoading: isLoading });
    };
    return loading ? (React.createElement(LoadingIndicator, null)) : (React.createElement(RatesSettingsLayout, { activeTab: "drayageRateCard", account: account },
        React.createElement(Label, { className: "text-lg" }, t('GENERATE_RATE_CARD')),
        React.createElement(Card, { className: "w-full mt-2 p-4" },
            React.createElement("div", { className: "w-full flex flex-col sm:flex-row justify-between" }, renderRecentRateCard())),
        rateCardEmailVisible && (React.createElement(SendRateCardEmailModal, { show: rateCardEmailVisible, onHide: closeRateCardEmailModal, onSend: sendRateCardEmail, defaultRecipient: getRateCardEmailRecipientList(account) })),
        React.createElement(Card, { className: "w-full mt-8 p-4" },
            React.createElement(Label, { className: "my-4 text-md" }, t('LANE_RATES_INCLUDED')),
            renderLaneRatesTable())));
};
export default RateCard;
